import { apiFetch } from "@/auth/apiClient";

export const ExamTypeBackend = {
  recorded: "CHAPTER",
  live: "FINAL",
  mock: "MOCK",
} as const;

export type ExamTypeBackend = "CHAPTER" | "FINAL" | "MOCK";

export const QuestionTypeBackend = {
  mcq: "MCQ",
  writing: "WRITING",
  listening: "LISTENING",
} as const;

export type QuestionTypeBackend = "MCQ" | "WRITING" | "LISTENING";

export type Exam = {
  id: string;
  courseId: string;
  chapterId: string | null;
  type: ExamTypeBackend;
  duration: number;
  totalMarks: number;
  passMarks: number;
  maxAttempts: number;
  availableFrom?: string | null;
  availableTo?: string | null;
  title?: string | null;
  description?: string | null;
  isPublished?: boolean;
};

export type QuestionScenario = {
  id: string;
  examId?: string | null;
  mockTestId?: string | null;
  instructionsText: string | null;
  imageUrl: string | null;
  audioUrl: string | null;
  sortOrder: number;
};

export type ExamQuestion = {
  id: string;
  examId: string;
  scenarioId: string | null;
  scenario?: QuestionScenario | null;
  sortOrder?: number;
  type: QuestionTypeBackend;
  questionText: string;
  options: Record<string, unknown> | null;
  correctAnswer: string | null;
  marks: number;
  imageUrl?: string | null;
  audioUrl?: string | null;
  answerExplanation?: string | null;
};

export type ExamAttempt = {
  id: string;
  userId: number;
  examId: string;
  score: string;
  isPassed: boolean;
  attemptNumber: number;
  answers?: Record<string, unknown> | null;
  startedAt?: string;
  submittedAt?: string | null;
  endsAt?: string | null;
  lastSavedAt?: string | null;
};

export type CourseExamsListResponse = {
  items: Exam[];
  total: number;
  page: number;
  limit: number;
};

export async function getCourseExams(
  courseId: string,
  params: { page?: number; limit?: number } = {},
): Promise<CourseExamsListResponse> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 100;
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
  return apiFetch<CourseExamsListResponse>(
    `/lms/courses/${courseId}/exams?${qs.toString()}`,
    { method: "GET" },
  );
}

export async function createExam(
  courseId: string,
  dto: {
    type: ExamTypeBackend;
    chapterId?: string | null;
    duration: number;
    totalMarks: number;
    passMarks: number;
    maxAttempts?: number;
    availableFrom?: string;
    availableTo?: string;
    title?: string;
    description?: string;
    isPublished?: boolean;
  },
): Promise<Exam> {
  return apiFetch<Exam>(`/lms/courses/${courseId}/exams`, { method: "POST", json: dto });
}

export async function updateExam(
  courseId: string,
  examId: string,
  dto: Partial<Exam>,
): Promise<Exam> {
  return apiFetch<Exam>(`/lms/courses/${courseId}/exams/${examId}`, { method: "PATCH", json: dto });
}

export async function getExamScenarios(courseId: string, examId: string): Promise<QuestionScenario[]> {
  return apiFetch<QuestionScenario[]>(
    `/lms/courses/${courseId}/exams/${examId}/scenarios`,
    { method: "GET" },
  );
}

export async function createExamScenario(
  courseId: string,
  examId: string,
  dto: {
    instructionsText?: string;
    imageUrl?: string;
    audioUrl?: string;
    sortOrder?: number;
  },
): Promise<QuestionScenario> {
  return apiFetch<QuestionScenario>(
    `/lms/courses/${courseId}/exams/${examId}/scenarios`,
    { method: "POST", json: dto },
  );
}

export async function deleteExamScenario(courseId: string, examId: string, scenarioId: string) {
  return apiFetch(`/lms/courses/${courseId}/exams/${examId}/scenarios/${scenarioId}`, { method: "DELETE" });
}

export async function patchExamScenario(
  courseId: string,
  examId: string,
  scenarioId: string,
  dto: {
    instructionsText?: string | null;
    imageUrl?: string | null;
    audioUrl?: string | null;
    sortOrder?: number;
  },
): Promise<QuestionScenario> {
  return apiFetch<QuestionScenario>(`/lms/courses/${courseId}/exams/${examId}/scenarios/${scenarioId}`, {
    method: "PATCH",
    json: dto,
  });
}

export async function getExamQuestions(
  courseId: string,
  examId: string,
): Promise<ExamQuestion[]> {
  return apiFetch<ExamQuestion[]>(
    `/lms/courses/${courseId}/exams/${examId}/questions`,
    { method: "GET" },
  );
}

export async function addExamQuestion(
  courseId: string,
  examId: string,
  dto: {
    type: QuestionTypeBackend;
    questionText: string;
    options?: Record<string, unknown>;
    correctAnswer?: string;
    marks: number;
    scenarioId?: string;
    sortOrder?: number;
    imageUrl?: string;
    audioUrl?: string;
  },
): Promise<ExamQuestion> {
  return apiFetch<ExamQuestion>(
    `/lms/courses/${courseId}/exams/${examId}/questions`,
    { method: "POST", json: dto },
  );
}

export async function patchExamQuestion(
  courseId: string,
  examId: string,
  questionId: string,
  dto: {
    type?: QuestionTypeBackend;
    questionText?: string;
    options?: Record<string, unknown>;
    correctAnswer?: string;
    marks?: number;
    scenarioId?: string | null;
    sortOrder?: number;
    imageUrl?: string | null;
    audioUrl?: string | null;
    answerExplanation?: string | null;
  },
): Promise<ExamQuestion> {
  return apiFetch<ExamQuestion>(
    `/lms/courses/${courseId}/exams/${examId}/questions/${questionId}`,
    { method: "PATCH", json: dto },
  );
}

export async function deleteExamQuestion(
  courseId: string,
  examId: string,
  questionId: string,
): Promise<void> {
  await apiFetch(`/lms/courses/${courseId}/exams/${examId}/questions/${questionId}`, {
    method: "DELETE",
  });
}

export async function startExam(courseId: string, examId: string): Promise<ExamAttempt> {
  return apiFetch<ExamAttempt>(
    `/lms/courses/${courseId}/exams/${examId}/start`,
    { method: "POST" },
  );
}

export async function regradeCourseExamAttempts(
  courseId: string,
  examId: string,
  dryRun: boolean = true,
): Promise<import('./mock-tests').RegradeResult> {
  return apiFetch<import('./mock-tests').RegradeResult>(
    `/lms/courses/${courseId}/exams/${examId}/regrade?dryRun=${dryRun}`,
    { method: "POST" },
  );
}

export async function getExamAttempt(attemptId: string): Promise<ExamAttempt> {
  return apiFetch<ExamAttempt>(`/lms/exam-attempts/${attemptId}`, { method: "GET" });
}

export async function getMyAttempts(courseId: string, examId: string): Promise<ExamAttempt[]> {
  return apiFetch<ExamAttempt[]>(`/lms/courses/${courseId}/exams/${examId}/attempts`, { method: "GET" });
}

export async function saveExamProgress(
  attemptId: string,
  body: { answers: Record<string, unknown> },
): Promise<ExamAttempt> {
  return apiFetch<ExamAttempt>(`/lms/exam-attempts/${attemptId}/progress`, {
    method: "PATCH",
    json: body,
  });
}

export async function submitExam(
  attemptId: string,
  body: { answers: Record<string, unknown> },
): Promise<ExamAttempt> {
  return apiFetch<ExamAttempt>(
    `/lms/exam-attempts/${attemptId}/submit`,
    { method: "POST", json: body },
  );
}

export async function getAttemptReview(courseId: string, examId: string, attemptId: string): Promise<ExamQuestion[]> {
  const data = await apiFetch<ExamQuestion[]>(`/lms/courses/${courseId}/exams/${examId}/attempts/${attemptId}/review`, { method: "GET" });
  return data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export async function getSignedAudioUrl(
  attemptId: string,
  s3Key: string,
): Promise<{ signedUrl: string; expiresIn: number }> {
  return apiFetch<{ signedUrl: string; expiresIn: number }>(
    `/lms/exam-attempts/${attemptId}/audio/token`,
    {
      method: "POST",
      json: { s3Key },
    },
  );
}
